/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useOrganizationId } from 'hooks/useOrganizationId';
import { AudienceEstimate, AudienceSelection } from 'model/emailMarketing';
import {
    fetchAudienceEstimateThunk,
    fetchAudienceGroupCountersThunk
} from 'actions/emailMarketing/EmailMarketingActions';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { RecipientEstimate, UNKNOWN_ESTIMATE } from '../audience';
import { toAudience, toAudienceSelection } from '../apiAdapters';
import { CampaignDraft, EmailLocation, RecipientType } from '../types';

const DEBOUNCE_MS = 300;
const CARD_TYPES: Array<RecipientType> = ['all_profiles', 'leads', 'all_clients'];

export interface AudienceTagOption {
    id: string;
    name: string;
    count: number;
}

export interface AudienceData {
    estimate: RecipientEstimate;
    // Recipients per card for the selected locations (null while loading).
    cardCounts: Partial<Record<RecipientType, number>> | null;
    tagOptions: Array<AudienceTagOption>;
    // Recipients before manual picks; Edit is disabled when it is 0.
    poolSize: number;
    loading: boolean;
    // Audience in the API shape, for the recipients drawer (API source).
    selection: AudienceSelection;
}

const toRecipientEstimate = (
    estimate: AudienceEstimate,
    pool: AudienceEstimate,
    locations: Array<EmailLocation>
): RecipientEstimate => ({
    known: true,
    total: estimate.totalRecipients,
    duplicates: estimate.duplicatesRemoved,
    unsubscribed: estimate.unsubscribedExcluded,
    excluded: Math.max(pool.totalRecipients - estimate.totalRecipients, 0),
    byLocation: estimate.byLocation.map(row => ({
        location: locations.find(location => location.id === row.locationId) ?? {
            id: row.locationId,
            name: row.locationName,
            region: '',
            city: ''
        },
        count: row.recipients
    }))
});

// Recipient counts for the Audience step, from the audience endpoints (CUD-5778).
export const useAudienceData = (draft: CampaignDraft): AudienceData => {
    const dispatch = useDispatch();
    const { locations } = useEmailMarketing();
    const organizationId = useOrganizationId();

    const selection = useMemo(() => toAudienceSelection(draft), [draft]);
    const needsTags = draft.recipientType === 'client_groups' && draft.tags.length === 0;
    const canQuery = !!organizationId && draft.locationIds.length > 0;
    const locationsKey = draft.locationIds.join(',');

    const [apiEstimate, setApiEstimate] = useState<RecipientEstimate>(UNKNOWN_ESTIMATE);
    const [apiPoolSize, setApiPoolSize] = useState(0);
    const [apiCardCounts, setApiCardCounts] = useState<AudienceData['cardCounts']>(null);
    const [apiTags, setApiTags] = useState<Array<AudienceTagOption>>([]);
    const [loading, setLoading] = useState(false);

    // Selection estimate plus the same audience without manual picks (to know how many were left out).
    const selectionKey = JSON.stringify(selection);
    useEffect(() => {
        if (!canQuery || needsTags) {
            setApiEstimate(UNKNOWN_ESTIMATE);
            setApiPoolSize(0);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(async () => {
            const pool: AudienceSelection = { ...selection, excludeIds: [], customerIds: null };
            try {
                const [estimate, poolEstimate] = (await Promise.all([
                    dispatch(fetchAudienceEstimateThunk(organizationId as number, selection) as any),
                    dispatch(fetchAudienceEstimateThunk(organizationId as number, pool) as any)
                ])) as unknown as [AudienceEstimate, AudienceEstimate];
                if (cancelled) return;
                setApiEstimate(toRecipientEstimate(estimate, poolEstimate, locations));
                setApiPoolSize(poolEstimate.totalRecipients);
            } catch {
                if (!cancelled) setApiEstimate(UNKNOWN_ESTIMATE);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canQuery, needsTags, selectionKey]);

    // Card counts and tag counts only depend on the locations.
    useEffect(() => {
        if (!canQuery) {
            setApiCardCounts(null);
            setApiTags([]);
            return;
        }
        let cancelled = false;
        const base: AudienceSelection = { ...selection, criteria: 'all', tags: [], excludeIds: [], customerIds: null };
        Promise.all([
            ...CARD_TYPES.map(type =>
                dispatch(
                    fetchAudienceEstimateThunk(organizationId as number, { ...base, audience: toAudience(type) }) as any
                )
            ),
            dispatch(
                fetchAudienceGroupCountersThunk(organizationId as number, { ...base, audience: 'customers' }) as any
            )
        ] as Array<Promise<any>>)
            .then(results => {
                if (cancelled) return;
                const counts: Partial<Record<RecipientType, number>> = {};
                CARD_TYPES.forEach((type, index) => {
                    counts[type] = (results[index] as AudienceEstimate).totalRecipients;
                });
                setApiCardCounts(counts);
                setApiTags(
                    results[CARD_TYPES.length].byTag.map((tag: { tag: string; name: string; count: number }) => ({
                        id: tag.tag,
                        name: tag.name,
                        count: tag.count
                    }))
                );
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canQuery, locationsKey]);

    return {
        estimate: apiEstimate,
        cardCounts: apiCardCounts,
        tagOptions: apiTags,
        poolSize: apiPoolSize,
        loading,
        selection
    };
};

export default useAudienceData;
